/** React glue for the web i18n instance (see ./index.ts). */
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { client } from "../api/client"
import i18n, { browserDefault, localeFor, toLang } from "./index"

/**
 * Changes whenever a signed-in session starts or ends (the shell mounts or
 * unmounts). Work begun in one session — e.g. a language save still in
 * flight — checks it before touching the global language.
 */
let sessionEpoch = 0
export function currentSession(): number {
  return sessionEpoch
}

/**
 * Applies the signed-in user's saved interface language; without a usable
 * preference (none saved, unsupported, or unreadable) the browser default.
 * Leaving the shell (sign-out) restores the browser default too, so one
 * user's choice never carries over to the next user of the same tab.
 */
export function LanguageSync() {
  useEffect(() => {
    sessionEpoch += 1
    let cancelled = false
    const apply = (lang: string) => {
      if (!cancelled && lang !== i18n.language) void i18n.changeLanguage(lang)
    }
    client.config
      .get("language")
      .then((res) => apply(toLang(res.value) ?? browserDefault()))
      .catch(() => apply(browserDefault()))
    return () => {
      cancelled = true
      sessionEpoch += 1
      if (i18n.language !== browserDefault()) void i18n.changeLanguage(browserDefault())
    }
  }, [])
  return null
}

/** Locale for dates and numbers in the current interface language. */
export function useLocale(): string {
  const { i18n: inst } = useTranslation()
  return localeFor(inst.language)
}
